//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { Dictionary, KeyType, LocalizationHelper, LogHelper, Md5, TypeHelper } from '../../../helpers'
import { SequentialItemSaver, SequentialItemSaverResult, SequentialItemSaverProgress, SequentialItemSaverStatus } from '../../../helpers/sequential-item-saver'

import { Enums } from '../../../models/common'
import { OpDataType } from '../../../models/core'
import { IOpData, IReferenceType } from '../../../models/util/interfaces'
import { UserSessionService } from '../../../services/user-session/user-session.service'
import { ColumnModel } from '../../api/api.models'
import { IIMRDataTypePanel, IMRDataTypePanelFieldValueChanged, IMRDataTypePanelOptions } from '../../common/data-type-panel/interfaces'
import { GridController, GridOptions, GridTypes } from '../../common/grid/grid.controller'
import { CreateRelatedItemsColumnsOptions, CreateRelatedItemsDataSource, CreateRelatedItemsOptions, ICreateRelatedItems, CreateRelatedItemsItemStatus } from './create-related-items.interfaces'

export class CreateRelatedItemsController<TSource, TTarget>
    implements ng.IComponentController
    , ICreateRelatedItems<TSource, TTarget>
{
    // #region Consts

    private static get SourceItemsGridName(): string { return 'CreateRelatedItemsGrid' }

    private static get FieldNameSavingProgress(): string { return '$ctrl.savingProgress' }

    private static get SUCCESS_TEXT(): string { return 'successText' }
    private static get ERROR_TEXT(): string { return 'errorText' }
    private static get WARNING_TEXT(): string { return 'warningText' }

    private static get SUCCESS_COLOR(): string { return '#02E6A1' }
    private static get ERROR_COLOR(): string { return '#F7484E' }
    private static get WARNING_COLOR(): string { return '#FFA500' }

    private static get LOG_PREFIX(): string { return 'CreateRelatedItems' }

    // #endregion

    // #region Localization

    public get savingSummaryText(): string { return LocalizationHelper.getLocalizedText('SavingSummary') }

    // #endregion

    // #region Members

    public savingProgress: number

    public isStarted: boolean
    public isLoading: boolean

    public options: CreateRelatedItemsOptions<TSource, TTarget>
    public dataTypePanelOptions: IMRDataTypePanelOptions
    public gridOptions: GridOptions

    public popupOptions: DevExpress.ui.dxPopupOptions

    public buttonStartOptions: DevExpress.ui.dxButtonOptions
    public buttonStopOptions: DevExpress.ui.dxButtonOptions
    public buttonFinishOptions: DevExpress.ui.dxButtonOptions

    public progressBarOptions: DevExpress.ui.dxProgressBarOptions
    public loadIndicatorOptions: DevExpress.ui.dxLoadIndicatorOptions

    private popup: DevExpress.ui.dxPopup
    private dataTypePanel: IIMRDataTypePanel
    private gridController: GridController<TSource>
    private grid: DevExpress.ui.dxDataGrid
    private progressBar: DevExpress.ui.dxProgressBar

    private buttonStart: DevExpress.ui.dxButton
    private buttonStop: DevExpress.ui.dxButton
    private buttonFinish: DevExpress.ui.dxButton

    private loadIndicator: DevExpress.ui.dxLoadIndicator

    private isDetailsRowsExpanded: boolean
    private dataTypeDictionary: Dictionary<OpDataType>
    private dataSource: CreateRelatedItemsDataSource<TSource>

    private itemSaver: SequentialItemSaver<TTarget>

    private previousMd5: string

    private statusById: CreateRelatedItemsItemStatus[] = []

    // #endregion

    // #region Ctor

    constructor(
        protected userSession: UserSessionService
    ) {

    }

    // #endregion

    // #region ng.IComponentController

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion

    // #region Events

    private onInitialize(): void {
        if (this.options != null && this.options.onInitialize != null) {
            this.options.onInitialize(this);
        }
    }

    private onPopupInitialized(args: any): void {
        this.popup = args.component
    }

    private onPopupHiding(args: any): void {
        this.hide()
    }

    private onPopupHidden(args: any) {
        this.reset()
    }

    private onSaveProgress(progress: SequentialItemSaverProgress<TTarget>): void {
        if (this.dataSource != null && progress != null && progress.index < _.size(this.dataSource.sourceItems)) {
            let status: CreateRelatedItemsItemStatus

            switch (progress.status) {
                case SequentialItemSaverStatus.Succes: {
                    status = {
                        field: CreateRelatedItemsController.SUCCESS_TEXT,
                        value: CreateRelatedItemsController.SUCCESS_TEXT
                    }
                    break
                }
                case SequentialItemSaverStatus.Error: {
                    status = {
                        field: CreateRelatedItemsController.ERROR_TEXT,
                        value: progress.error
                    }
                    break
                }
                case SequentialItemSaverStatus.Warning: {
                    status = {
                        field: CreateRelatedItemsController.WARNING_TEXT,
                        value: progress.error
                    }
                    break
                }
                case SequentialItemSaverStatus.Unknown:
                default: {
                    break
                }
            }

            this.statusById[this.options.sourceOptions.keyProvider(this.dataSource.sourceItems[progress.index])] = status

            const sourceItem: TSource = this.dataSource.sourceItems[progress.index]

            let validationStatus: Enums.ValidationStatus = Enums.ValidationStatus.Unknown

            if (status != null) {
                if (status.field === CreateRelatedItemsController.SUCCESS_TEXT && !TypeHelper.isNullOrEmpty(status.value)) {

                    validationStatus = Enums.ValidationStatus.Correct
                } else if (status.field === CreateRelatedItemsController.ERROR_TEXT && !TypeHelper.isNullOrEmpty(status.value)) {
                    validationStatus = Enums.ValidationStatus.Incorrect
                } else if (status.field === CreateRelatedItemsController.WARNING_TEXT && !TypeHelper.isNullOrEmpty(status.value)) {
                    validationStatus = Enums.ValidationStatus.Warning
                }
            }

            this.gridController.setItemDisplayState(sourceItem, { validation: validationStatus })

            this.savingProgress++
        }
    }

    private onSaveFinished(progress: SequentialItemSaverResult<TTarget>): void {
        if (progress != null && _.size(progress.progressItems)) {
            progress.progressItems.forEach((item: SequentialItemSaverProgress<TTarget>) => {
                this.onSaveProgress(item)
            })
        }

        this.isStarted = false

        this.setButtonsFinished()
        this.setLoadIndicatorVisibility(false)
    }

    private onStartClick(args: any): void {
        if (this.itemSaver == null || this.itemSaver.isStartable) {
            this.savingProgress = 0

            this.setLoadIndicatorVisibility(true)
            this.setButtonsProgress()
            this.clearRowsDetails()

            this.itemSaver = new SequentialItemSaver({
                itemsToSave: this.getItemsToSave(),
                saveDelegate: this.options.targetOptions.saveDelegate,
                progressDelegate: (progress: SequentialItemSaverProgress<TTarget>) => { this.onSaveProgress(progress) },
                finishedDelegate: (progress: SequentialItemSaverResult<TTarget>) => { this.onSaveFinished(progress) }
            })

            this.itemSaver.start()

            this.isStarted = true
        }
    }

    private onStopClick(args: any): void {
        this.itemSaver.cancel();
    }

    private onFinishClick(args: any): void {
        this.itemSaver.cancel();
        this.hide()
    }

    private onDataTypePanelInitialized(controller: IIMRDataTypePanel): void {
        this.dataTypePanel = controller
    }

    private onDataTypePanelFormContentReady(controller: IIMRDataTypePanel): void {
        this.validate()
    }

    private onGridInitialized(args: any, controller: GridController<TSource>): void {
        this.grid = args.component;

        this.gridController = controller
    }

    private onGridRowPrepared(args: any): void {
        if (args.rowType == "data") {
            const statusById = this.statusById[args.data.id]

            if (statusById != null) {
                if (statusById.field === CreateRelatedItemsController.ERROR_TEXT && !TypeHelper.isNullOrEmpty(statusById.value)) {
                    args.isExpanded = this.isDetailsRowsExpanded
                } else if (statusById.field === CreateRelatedItemsController.WARNING_TEXT && !TypeHelper.isNullOrEmpty(statusById.value)) {
                    args.isExpanded = this.isDetailsRowsExpanded
                }
            }
        }
    }

    private onGridToggleDetailsRows(isExpanded: boolean): void {
        this.isDetailsRowsExpanded = isExpanded
    }

    private onFinishButtonInitialized(args: any): void {
        this.buttonFinish = args.component

        this.setButtonsFinished()
    }

    private onStopButtonInitialized(args: any): void {
        this.buttonStop = args.component

        this.setButtonsFinished()
    }

    private onStartButtonInitialized(args: any): void {
        this.buttonStart = args.component

        this.setButtonsFinished()
    }

    private onDataTypePanelFieldValueChanged(args: IMRDataTypePanelFieldValueChanged): void {
        if (this.dataTypePanel != null && this.dataTypePanel.validate()) {
            const itemsToSave: TTarget[] = this.getItemsToSave()
            const currentMd5: string = Md5.init(itemsToSave)

            if (this.previousMd5 != currentMd5) {
                this.previousMd5 = currentMd5
                this.setButtonsStartable()
            }
        } else {
            this.setButtonsFinished()
        }
    }

    private onLoadIndicatorInitialized(args: any): void {
        this.loadIndicator = args.component
    }

    // #endregion

    // #region Lifecycle

    private initialize(): JQueryPromise<void> {
        this.isLoading = true

        this.savingProgress = 0

        return this
            .setOptions()
            .then(() => {
                this.setLoadIndicatorOptions()
                this.setPopupOptions()
                this.setButtonOptions()
                this.setProgressBarOptions()

                return this.setDataTypePanelOptions()
            })
            .then(() => {
                return this.setGridOptions()
            })
            .always(() => {
                this.isLoading = false
                this.onInitialize()
            })
    }

    // #endregion

    // #region ILocationRelatedItems implementation

    public show(): void {
        if (this.popupExists()) {
            this.initialize()
                .then(() => {
                    this.popup.show()
                })
        } else {
            LogHelper.error(CreateRelatedItemsController.LOG_PREFIX, `Can't show popup - popup doesn't exist`)
        }
    }

    public hide(): void {
        if (this.popupExists()) {
            this.popup.hide()
        } else {
            LogHelper.error(CreateRelatedItemsController.LOG_PREFIX, `Can't hide popup - popup doesn't exist`)
        }
    }

    public setDataSource(dataSource: CreateRelatedItemsDataSource<TSource>): void {
        this.statusById = []
        this.dataSource = dataSource
    }

    // #endregion

    // #region Setup

    private getGridButtonsOptions(): any {
        return {
            enabled: true,
            toggleDetailsRowsButtonVisible: true,
            onToggleDetailsRows: (isExpanded: boolean): void => { this.onGridToggleDetailsRows(isExpanded) }
        }
    }

    private getGridMasterDetailOptions(): any {
        return {
            enabled: true,
            template: (container: any, options: any) => {
                const statusById: CreateRelatedItemsItemStatus = this.statusById[options.data.id]

                if (statusById != null
                    && (statusById.field == CreateRelatedItemsController.ERROR_TEXT
                        || statusById.field == CreateRelatedItemsController.WARNING_TEXT)
                ) {
                    container.append($('<div ><p>' + statusById.value + '</p></div>'));
                }
            },
            autoExpandAll: this.isDetailsRowsExpanded
        }
    }

    private getGridEditingOptions(): any {
        return {
            mode: 'cell',
            allowAdding: false,
            allowUpdating: false,
            allowDeleting: false
        }
    }

    private setOptions(): JQueryPromise<void> {
        return this.userSession
            .getDataProvider()
            .System
            .getDictionaries([Enums.ReferenceType.IdDataType])
            .then((dictionaries: Dictionary<Dictionary<IReferenceType>>) => {
                if (dictionaries != null && dictionaries.containsKey(Enums.ReferenceType.IdDataType)) {
                    this.dataTypeDictionary = new Dictionary<OpDataType>(null, KeyType.Number)

                    dictionaries
                        .tryGetValue(Enums.ReferenceType.IdDataType)
                        .values()
                        .map((dt: IReferenceType) => dt as OpDataType)
                        .forEach((dt: OpDataType) => this.dataTypeDictionary.addOrUpdate(dt.idDataType, dt))
                }
            })
    }

    private setPopupOptions(): void {
        this.popupOptions = {
            closeOnOutsideClick: true,
            fullScreen: true,
            showTitle: true,

            title: this.options.popupTitle,

            width: '100%',
            height: '100%',

            onInitialized: (args: any) => { this.onPopupInitialized(args); },
            onHiding: (args: any) => { this.onPopupHiding(args) },
            onHidden: (args: any) => { this.onPopupHidden(args) }
        }
    }

    private setButtonOptions(): void {
        this.buttonStartOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.StartProcess),

            onClick: (args: any) => { this.onStartClick(args) },
            onInitialized: (args: any) => { this.onStartButtonInitialized(args); }
        }

        this.buttonStopOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Stop),

            onClick: (args: any) => { this.onStopClick(args) },
            onInitialized: (args: any) => { this.onStopButtonInitialized(args); }
        }

        this.buttonFinishOptions = {
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Finish),

            onClick: (args: any) => { this.onFinishClick(args) },
            onInitialized: (args: any) => { this.onFinishButtonInitialized(args); }
        }
    }

    private setProgressBarOptions(): void {
        this.progressBarOptions = {
            visible: true,

            value: 0,
            min: 0,
            max: this.dataSource == null || this.dataSource.sourceItems == null ? 100 : this.dataSource.sourceItems.length,

            statusFormat: (ratio: number, value: number) => { return this.progressStatusFormat(ratio, value); },

            bindingOptions: {
                value: CreateRelatedItemsController.FieldNameSavingProgress
            }
        }
    }

    private setDataTypePanelOptions(): JQueryPromise<void> {
        const columns: CreateRelatedItemsColumnsOptions = this.options != null && this.options.targetOptions != null
            ? this.options.targetOptions.columns
            : null

        const requiredColumns: CreateRelatedItemsColumnsOptions = this.options != null && this.options.targetOptions != null
            ? this.options.targetOptions.requiredColumns
            : null

        return $.when(
            this.getDataTypeFromColumns(columns),
            this.getDataTypeFromColumns(requiredColumns)
        )
            .then((fields: OpDataType[], required: OpDataType[]) => {
                this.dataTypePanelOptions = {
                    columnCount: 1,

                    opDataTypes: {
                        params: fields,
                        required: required
                    },

                    onInitialized: (controller: IIMRDataTypePanel) => { this.onDataTypePanelInitialized(controller) },
                    onFormContentReady: (controller: IIMRDataTypePanel) => { this.onDataTypePanelFormContentReady(controller) },
                    onFieldValueChanged: (args: IMRDataTypePanelFieldValueChanged) => { this.onDataTypePanelFieldValueChanged(args) }
                }
            })
    }

    private setGridOptions(): JQueryPromise<void> {
        const promises: JQueryPromise<any[]>[] = []

        if (this.dataSource != null && _.size(this.dataSource.columns) > 0) {
            promises.push($.Deferred<ColumnModel[]>().resolve(this.dataSource.columns).promise())
        } else if (this.dataSource != null && _.size(this.dataSource.dxColumns) > 0) {
            promises.push($.Deferred<ColumnModel[]>().resolve([]).promise())
            promises.push($.Deferred<ColumnModel[]>().resolve([]).promise())
            promises.push($.Deferred<DevExpress.ui.dxDataGridColumn[]>().resolve(this.dataSource.dxColumns).promise())
        } else {
            promises.push(this.createGridColumnsPromise(this.options.gridOptions.columns))
            promises.push(this.createGridColumnsPromise(this.options.gridOptions.defaultColumns))
        }

        return $.when<any>(...promises)
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[], dxColumns: DevExpress.ui.dxDataGridColumn[]) => {
                const allowColumnReordering: boolean = this.options.gridOptions.allowColumnReordering === null
                    || this.options.gridOptions.allowColumnReordering === undefined
                    || this.options.gridOptions.allowColumnReordering

                this.gridOptions = <any>{
                    columnAutoWidth: true,
                    addCustomFilters: false,
                    ignoreKey: true,

                    columns: dxColumns,
                    columnsDictionary: columns,
                    defaultColumnsDictionary: defaultColumns || columns,

                    dataSourceParser: this.options.gridOptions.dataSourceParser,

                    exportOptions: null,
                    importOptions: null,
                    idReferenceType: this.options.gridOptions.referenceType,

                    localStorageVarForSavedEntity: null,
                    name: CreateRelatedItemsController.SourceItemsGridName,

                    patternsProviderOptions: null,
                    preExecuteClearFilter: null,
                    referenceId: this.options.gridOptions.referenceType,
                    type: GridTypes.PAGINATED_SMALL,

                    dataSource: this.dataSource == null ? [] : this.dataSource.sourceItems,

                    allowColumnReordering: allowColumnReordering,

                    editing: this.getGridEditingOptions(),
                    masterDetail: this.getGridMasterDetailOptions(),
                    buttonsOptions: this.getGridButtonsOptions(),

                    onInitialized: (args: any, grid: GridController<TSource>) => { this.onGridInitialized(args, grid) },
                    onRowPrepared: (args: any) => { this.onGridRowPrepared(args) },
                }
            })
            .fail((error: any) => {
                LogHelper.error(CreateRelatedItemsController.LOG_PREFIX, error)
            })
    }

    private setLoadIndicatorOptions(): void {
        this.loadIndicatorOptions = {
            visible: false,
            height: 25,
            width: 25,
            onInitialized: (args: any) => { this.onLoadIndicatorInitialized(args) }
        }
    }

    // #endregion

    // #region CreateRelatedItemsController

    private popupExists(): boolean {
        return this.popup != null
    }

    private progressStatusFormat(ratio: number, value: number) {
        const progressValue: number = Math.round(ratio * 100);
        return `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Progress)}: ${progressValue}%`
    }

    private createGridColumnsPromise(options: CreateRelatedItemsColumnsOptions): JQueryPromise<ColumnModel[]> {
        if (options != null) {
            if (_.size(options.columns) > 0) {
                return $.Deferred<ColumnModel[]>().resolve(options.columns).promise()
            }

            if (options.groupCategory != null) {
                const requestOptions: {
                    groupReferenceType?: Enums.ReferenceType,
                    groupReferenceValue?: number,
                    viewReferenceType?: Enums.ReferenceType
                } = options.groupReferenceType == null ? {} : {
                    groupReferenceType: options.groupReferenceType,
                    groupReferenceValue: options.groupReferenceValue,
                    viewReferenceType: null,
                }

                return this.userSession
                    .getDataProvider()
                    .System
                    .getRequestColumnInfo(options.groupCategory, options.groupModuleName, requestOptions)
            }

            if (_.size(options.dataTypes) > 0) {
                const columns: ColumnModel[] = options.dataTypes.map((dataType: OpDataType, index: number) => {
                    const column: ColumnModel = new ColumnModel()

                    column.id = dataType.idDataType
                    column.caption = dataType.toString()
                    column.name = dataType.name.toLowerCase()
                    column.type = dataType.idDataTypeClass
                    column.referenceTypeId = dataType.idReferenceType
                    column.isEditable = dataType.isEditable
                    column.format = <any>dataType.dataTypeFormat
                    column.unitId = dataType.idUnit
                    column.unitName = dataType.unit.name
                    column.dataType = dataType
                    column['orderNbr'] = index

                    return column
                })

                return $.Deferred<ColumnModel[]>().resolve(columns).promise()
            }
        }

        return $.Deferred<ColumnModel[]>().resolve([]).promise()
    }

    private getDataTypeFromColumns(options: CreateRelatedItemsColumnsOptions): JQueryPromise<OpDataType[]> {
        const deferred: JQueryDeferred<OpDataType[]> = $.Deferred<OpDataType[]>()

        if (options != null) {
            if (_.size(options.columns) > 0) {
                const dataTypeFromColumns: OpDataType[] = options.columns
                    .map((column: ColumnModel) => this.dataTypeDictionary.tryGetValue(column.id))
                    .filter((dataType: OpDataType) => dataType != null)

                deferred.resolve(dataTypeFromColumns)
            } else if (options.groupCategory != null) {
                const requestOptions: {
                    groupReferenceType?: Enums.ReferenceType,
                    groupReferenceValue?: number,
                    viewReferenceType?: Enums.ReferenceType
                } = options.groupReferenceType == null ? {} : {
                    groupReferenceType: options.groupReferenceType,
                    groupReferenceValue: options.groupReferenceValue,
                    viewReferenceType: null,
                }

                this.userSession
                    .getDataProvider()
                    .System
                    .getRequestColumnInfo(options.groupCategory, options.groupModuleName, requestOptions)
                    .then((columns: ColumnModel[]) => {
                        const dataTypeFromColumns: OpDataType[] = _.size(columns) == 0
                            ? []
                            : columns
                                .map((column: ColumnModel) => this.dataTypeDictionary.tryGetValue(column.id))
                                .filter((dataType: OpDataType) => dataType != null)

                        deferred.resolve(dataTypeFromColumns)
                    })
                    .fail((error: any) => {
                        LogHelper.error(CreateRelatedItemsController.LOG_PREFIX, error)
                        deferred.resolve([])
                    })
            } else if (_.size(options.dataTypes) > 0) {
                deferred.resolve(options.dataTypes)
            } else {
                deferred.resolve([])
            }
        } else {
            deferred.resolve([])
        }

        return deferred
            .promise()
            .then((dataTypes: OpDataType[]) => {
                return (dataTypes || []).filter((dataType: OpDataType) => {
                    if (_.size(options.hiddenDataTypes) > 0) {
                        return !options.hiddenDataTypes.some((hidden: OpDataType) => hidden.idDataType === dataType.idDataType)
                    }

                    return true
                })
            })
    }

    private getItemsToSave(): TTarget[] {
        return (this.dataSource.sourceItems || [])
            .map((sourceObj: TSource) => {
                const dataList: IOpData[] = this.dataTypePanel.getDataList().toArray()

                return this.options.targetOptions.itemFactory({
                    sourceObject: sourceObj,
                    dataList: dataList
                })
            })
    }

    private reset(): void {
        this.savingProgress = 0

        this.isStarted = false
        this.isLoading = false

        // Don't remove these commented out lines, so noone
        // add them nulled.
        //this.options = null // DON'T
        //this.popupOptions = null // DON'T

        this.dataTypePanelOptions = null
        this.gridOptions = null
        this.buttonStartOptions = null
        this.buttonStopOptions = null
        this.buttonFinishOptions = null
        this.progressBarOptions = null
        this.loadIndicatorOptions = null

        //this.popup = null // DON'T
        this.dataTypePanel = null
        this.gridController = null
        this.grid = null
        this.progressBar = null
        this.buttonStart = null
        this.buttonStop = null
        this.buttonFinish = null
        this.loadIndicator = null

        this.isDetailsRowsExpanded = false
        this.dataTypeDictionary = null
        this.dataSource = null
        this.statusById = null

        if (this.itemSaver != null) {
            this.itemSaver.cancel()
        }

        this.itemSaver = null

        this.previousMd5 = null
    }

    private clearRowsDetails(): void {
        this.statusById = []
        this.gridController.setDataSource(this.dataSource == null ? [] : this.dataSource.sourceItems);
        this.grid.refresh()
    }

    private setButtonsStartable(): void {
        this.setButtonDisabled(this.buttonStart, false)
        this.setButtonDisabled(this.buttonStop, true)
        this.setButtonDisabled(this.buttonFinish, false)
    }

    private setButtonsProgress(): void {
        this.setButtonDisabled(this.buttonStart, true)
        this.setButtonDisabled(this.buttonStop, false)
        this.setButtonDisabled(this.buttonFinish, false)
    }

    private setButtonsFinished(): void {
        this.setButtonDisabled(this.buttonStart, true)
        this.setButtonDisabled(this.buttonStop, true)
        this.setButtonDisabled(this.buttonFinish, false)
    }

    private setButtonDisabled(button: DevExpress.ui.dxButton, disabled: boolean): void {
        if (button != null) {
            button.option('disabled', disabled)
        }
    }

    private setLoadIndicatorVisibility(isVisible: boolean): void {
        if (this.loadIndicator != null) {
            this.loadIndicator.option('visible', isVisible)
        }
    }

    private validate(): void {
        if (this.dataTypePanel == null || this.dataTypePanel.validate()) {
            this.setButtonsStartable()
        } else {
            this.setButtonsFinished()
        }
    }

    // #endregion
}